/* eslint-disable no-console */
/* eslint-disable max-classes-per-file */
import { useCallback, useState } from 'react';
import {
  merge, upperFirst, endsWith, isEmpty, camelCase, mapValues, toPath, get,
} from 'lodash';
import { StatusCodes } from 'http-status-codes';
import { NavigateFunction } from 'react-router-dom';
import qs from 'qs';
import dayjs from 'dayjs';
import type { FormErrors } from '@mantine/form';
import { showNotification } from '@mantine/notifications';

import { ErrKey, ErrorResponse } from '../models';
import { PARAMETER_SPLITTER, TIMEZONE_HEADER_KEY } from '../constants';
import config from '../config';
import { useNavigateRef } from './use-navigate-ref';
import {
  ApiEndpoints, Endpoint, RequestClass, ResponseClass,
} from '../api-routes';
import { grp } from '../routes';

export class ApiError extends Error {
  errors: string[];

  validationErrors: FormErrors;

  status: number;

  source?: ErrKey;

  data?: { [key: string]: unknown };

  isHandled: boolean;

  constructor(data: ErrorResponse, status: number) {
    let { message } = data;
    message = upperFirst(message) + (endsWith(message, '.') ? '' : '.');
    super(message);

    this.status = status;
    this.errors = data.errors || [];
    this.validationErrors = {};
    this.source = data.source as ErrKey;
    this.data = data.data;
    this.isHandled = false;

    if (this.status === StatusCodes.UNPROCESSABLE_ENTITY) {
      this.prepareValidationErrors();
    }
  }

  hasValidationErrors(): boolean {
    return !isEmpty(this.validationErrors);
  }

  setHandled(): void {
    this.isHandled = true;
  }

  private prepareValidationErrors() {
    this.errors.forEach((v) => {
      const [field, , message] = v.split(PARAMETER_SPLITTER);
      let fieldPath = field?.split('.')
        .slice(1)
        .map((f) => f
          .replace(/\[[^\]]*\]/g, (match) => `[${camelCase(match.slice(1, -1))}]`) // köşeli parantez içindeki tüm değerler camelCase yapılıyor
          .replace(/[^[\]]+/g, (match) => camelCase(match))) // köşeli parantez dışında kalan değerler camelCase yapılıyor
        .join('.');

      fieldPath = toPath(fieldPath).join('.'); // @mantine/form nested fields support
      this.validationErrors[fieldPath] = message;
    });
  }
}

export class NetworkError extends Error {
  isHandled: boolean;

  constructor(message: string) {
    super(message);

    this.isHandled = false;
  }

  setHandled(): void {
    this.isHandled = true;
  }
}

const prepareErrors = (error: unknown) => {
  if (error instanceof ApiError) {
    throw error;
  }

  if (error instanceof Error) {
    throw new NetworkError(error.message);
  }

  if (typeof error === 'object') {
    throw new NetworkError(JSON.stringify(error));
  }

  throw new NetworkError(String(error));
};

const handleCommonErrors = ({
  error, navigate, endpointName, silent = false,
}: {
  error: unknown,
  navigate: NavigateFunction,
  endpointName: Endpoint,
  silent?: boolean,
}) => {
  if (error instanceof NetworkError) {
    error.setHandled();

    if (!silent) {
      showNotification({
        id: 'network-generic-error',
        color: 'red',
        message: 'There may be a problem with the internet connection or the server. Please try again later.',
        autoClose: 10000,
      });
    }
    console.error(error);

    return;
  }

  if (error instanceof ApiError) {
    if (
      error.source === ErrKey.AuthMiddlewareErrKey
      && (error.status === StatusCodes.UNAUTHORIZED || error.status === StatusCodes.BAD_REQUEST)
    ) {
      error.setHandled();

      if (!silent) {
        showNotification({
          id: 'auth-session-terminated-error',
          color: 'red',
          message: `Session has been terminated. Please login again. (${error.message})`,
          autoClose: 8000,
        });
      }
      console.error(error);

      navigate(grp('logout'));
      return;
    }

    if (
      error.source === ErrKey.AuthHandlerErrKey
      && get(error, 'data.refreshTokensProcess', false)
      && (error.status === StatusCodes.UNAUTHORIZED || error.status === StatusCodes.BAD_REQUEST)
    ) {
      error.setHandled();

      if (!silent) {
        showNotification({
          id: 'auth-refresh-tokens-error',
          color: 'red',
          message: `Session has been terminated. Please login again. (${error.message})`,
          autoClose: 8000,
        });
      }
      console.error(error);

      navigate(grp('logout'));
      return;
    }

    if (
      error.source === ErrKey.GuardMiddlewareErrKey
    ) {
      error.setHandled();

      if (!silent) {
        showNotification({
          id: 'guard-middleware-error',
          color: 'red',
          message: error.message,
          autoClose: 8000,
        });
      }
      console.error(error);

      // guard-middleware proje sahibinin geçerli bir planı yoksa 402 döner
      if (
        error.status === StatusCodes.PAYMENT_REQUIRED
        && error.data?.isProjectOwner
      ) {
        navigate(grp('account', { activeTab: 'billing' }));
      }
    }
  }
};

interface RequestParams<TResponse, TRequest> {
  url: string;
  options: RequestInit;
  withAuth?: boolean;
  queryParams?: Partial<TRequest>;
  urlParams?: Record<string, unknown>;
  responseClass?: { new(source: unknown): TResponse; };
  isFormData?: boolean;
}

const request = async <TResponse, TRequest>({
  url, options, withAuth = false, queryParams, urlParams, responseClass, isFormData = false,
}: RequestParams<TResponse, TRequest>): Promise<TResponse> => {
  let data;
  let targetUrl = `${config.apiUrl}/${url}`;
  const defaultOptions: RequestInit = {
    headers: {
      [TIMEZONE_HEADER_KEY]: dayjs.tz.guess(),
    },
    cache: 'no-store',
    credentials: 'include',
  };
  const refreshTokensEndpoint = ApiEndpoints['auth.refresh-tokens'];
  const refreshTokensUrl = `${config.apiUrl}/${refreshTokensEndpoint.endpoint}`;

  // eslint-disable-next-line max-len
  // multipart/form-data header'ını hiç set etmiyoruz. boundary mevzusundan dolayı tarayıcının set etmesini bekliyoruz. https://stackoverflow.com/a/35206069/5918546
  if (!isFormData) {
    ((defaultOptions.headers) as Record<string, string>)['Content-Type'] = 'application/json';
  }

  if (urlParams) {
    Object.entries(urlParams).forEach(([param, val]) => {
      targetUrl = targetUrl.replace(`:${param}`, String(val));
    });
  }

  if (queryParams) {
    const formattedQueryParams = mapValues(queryParams ?? {}, (p: unknown) => {
      if (dayjs.isDayjs(p)) {
        return (p as dayjs.Dayjs).toISOString();
      }
      return p;
    });
    targetUrl += `?${qs.stringify(formattedQueryParams, { indices: false })}`;
  }

  const refreshTokensRequest = () => {
    if (window.USE_API_REFRESH_TOKENS_REQUEST) {
      return window.USE_API_REFRESH_TOKENS_REQUEST.then((response) => response.clone());
    }

    window.USE_API_REFRESH_TOKENS_REQUEST = fetch(refreshTokensUrl, merge(defaultOptions, {
      method: refreshTokensEndpoint.method,
      headers: {
        ...defaultOptions.headers,
        'Content-Type': 'application/json',
      },
    }));

    return window.USE_API_REFRESH_TOKENS_REQUEST.then((response) => {
      window.USE_API_REFRESH_TOKENS_REQUEST = null;
      return response.clone();
    });
  };

  const targetRequest = () => fetch(targetUrl, merge(defaultOptions, options));

  try {
    let response = await targetRequest();

    // parse json response
    if (response.status !== StatusCodes.NO_CONTENT) {
      data = await response.json();
    }

    // check api errors
    if (response.status >= StatusCodes.BAD_REQUEST) {
      // if unauthorized, refresh tokens and retry
      if (
        data.source === ErrKey.AuthMiddlewareErrKey
        && (response.status === StatusCodes.UNAUTHORIZED || response.status === StatusCodes.BAD_REQUEST)
      ) {
        // refresh tokens
        const refreshTokensResponse = await refreshTokensRequest();

        // parse json response
        let refreshTokensData;
        if (refreshTokensResponse.status !== StatusCodes.NO_CONTENT) {
          refreshTokensData = await refreshTokensResponse.json();
        }

        // check api errors
        if (refreshTokensResponse.status >= StatusCodes.BAD_REQUEST) {
          throw new ApiError(refreshTokensData, refreshTokensResponse.status);
        }

        // retry target request
        response = await targetRequest();

        // parse json response
        if (response.status !== StatusCodes.NO_CONTENT) {
          data = await response.json();
        }

        // check api errors
        if (response.status >= StatusCodes.BAD_REQUEST) {
          throw new ApiError(data, response.status);
        }
      } else {
        throw new ApiError(data, response.status);
      }
    }

    // data convert to response class
    if (responseClass) {
      // eslint-disable-next-line new-cap
      data = new responseClass(data);
    }
  } catch (error) {
    prepareErrors(error);
  }

  return data;
};

type UseApiOptions = {
  loadingInitialState?: boolean;
}

type UseApiCallParams<TRequest> = {
  params?: Partial<TRequest>;
  queryParams?: Partial<TRequest>;
  urlParams?: Record<string, unknown>;
  callOpts?: {
    noLoading?: boolean;
    noErrorDisplay?: boolean;
  };
}

type UseApi<T extends Endpoint> = [
  (P?: UseApiCallParams<RequestClass<T>>) => Promise<ResponseClass<T>>, // call method
  boolean, // loading state
  React.Dispatch<React.SetStateAction<boolean>>, // loading state setter
]

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useApi<T extends Endpoint>(
  endpointName: T,
  opts: UseApiOptions = {},
): UseApi<T> {
  const navigate = useNavigateRef();
  const [loading, setLoading] = useState(opts.loadingInitialState ?? false);

  const {
    endpoint, method, withAuth, isFormData, resc: responseClass,
  } = ApiEndpoints[endpointName];

  const apiCall = useCallback(async ({
    params, queryParams, urlParams, callOpts,
  }: UseApiCallParams<RequestClass<T>> = {}): Promise<ResponseClass<T>> => {
    try {
      if (!callOpts?.noLoading) { setLoading(true); }

      const requestOptions: RequestInit = { method };
      if (params) {
        if (isFormData) {
          const formData = new FormData();
          Object.entries(params).forEach(([key, value]) => {
            if (value) {
              formData.append(key, value as never);
            }
          });
          requestOptions.body = formData;
        } else {
          requestOptions.body = JSON.stringify(params);
        }
      }
      const data = await request<ResponseClass<T>, RequestClass<T>>({
        url: endpoint,
        options: requestOptions,
        withAuth,
        queryParams,
        urlParams,
        responseClass,
        isFormData,
      });

      if (!callOpts?.noLoading) { setLoading(false); }

      return data;
    } catch (error) {
      if (!callOpts?.noLoading) { setLoading(false); }

      handleCommonErrors({
        error,
        navigate: navigate.current,
        endpointName,
        silent: callOpts?.noErrorDisplay,
      });

      throw error;
    }
  }, [method, endpoint, withAuth, responseClass, isFormData, navigate, endpointName]);

  return [apiCall, loading, setLoading];
}
